/* eslint-disable max-lines */
import {
  Component, ViewEncapsulation, ChangeDetectionStrategy, Input,
  Output, EventEmitter, AfterViewInit, OnDestroy, ChangeDetectorRef
} from '@angular/core';
import { UntypedFormGroup, UntypedFormArray } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';
import { MarriottCreateFormUIResponse } from '@pax/entities/marriott-create-form';

import {
  TravelerPaxResponse, TravelerPax, TravelerPaxIdentificator, TravelerPaxResponseVm, LoyaltyPayload, LoyaltyResponse
} from '@pax/entities/traveler-pax';
import { TravelerInfoType, TravelerTypes } from '@pax/entities/traveler-info';
import { User } from '@app/modules/core/interfaces/user';
import { PaxFormService } from '@pax/services/pax-form.service';
import { DomManipulateService } from '@app/modules/core/services/dom-manipulate.service';
import { CountriesVm } from '@booking/entities/countries';
import { FrequentFlyerResponse } from '@app/entities/frequent-flyer';
import { BOOKING_FORM_INPUTS, INVALID, TRAVELER_FORM_INPUTS } from '@booking/constants/forms.constants';
import { AirValidationResponse } from '@app/entities/air-validation.response';
import { LoyaltyCreateEvent, LoyaltyUpdateEvent } from '@pax/entities/pax-ui';
import { TravelBookingType } from '@pax/entities/travel-booking-types';
import { AMPLITUDE_EVENTS } from '@app/modules/core/constants/amplitude.constants';
import { FormErrors } from '@reservation/entities/form-errors';
import { AmplitudeEventParams } from '@amplitude/interfaces/amplitude-events';
import { BusinessRulesByBookingType } from '@app/entities/booking-documents';
import { getBookingInfoTypeByTravelerInfoType, getTranslateKey } from '@pax/mappers/get-pax-info-by-types.mapper';
import { AVO_EVENTS } from '@avo/events/avo.events';
import { HotelProvider } from '@hotels/hotel-results/entities/hotel-searched';
import { isEnabledPaxDocumentModule } from '@pax/mappers/pax-traveler-documents.mapper';
import { LoyaltyProgramResponse, MarriottEnrollment, MarriottEnrollmentResponse } from '@pax/entities/loyalty-program.response';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { TypographyColor, TypographyFontSize } from '@design-system/components/atoms/typography/entities/typography.types';
import { AllProductsValidation } from '@app/entities/validation-types';

@Component({
  selector: 'app-pax-section',
  templateUrl: './pax-section.component.html',
  styleUrls: ['./pax-section.component.sass'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class PaxSectionComponent implements AfterViewInit, OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() paxTravelers: TravelerPaxResponse[];
  @Input() frequentFlyerPrograms: FrequentFlyerResponse[] = [];
  @Input() countries: CountriesVm[] = [];
  @Input() travelerBookingProvider: TravelBookingType = TravelBookingType.FLIGHTS;
  @Input() validator: AllProductsValidation;
  @Input() travelerType: TravelerTypes;
  @Input() travelerInfoType: TravelerInfoType;
  @Input() user: User;
  @Input() isSubmitted: boolean = false;
  @Input() isSubmittedSuccess: boolean = false;
  @Input() showFrequentFlyer: boolean;
  @Input() isMobile: boolean;
  @Input() isAnInternationalTrip: boolean;
  @Input() ordinalKey: string;
  @Input() ordinalNumber: number;
  @Input() travelerNumber: number;
  @Input() travelerAge: number | null = null;
  @Input() listUILength: number;
  @Input() businessRules: BusinessRulesByBookingType;
  @Input() productCode: string;
  @Input() productProvider: string;
  @Input() bookingSource: HotelProvider;
  @Input() marriottloyaltyNumber: LoyaltyProgramResponse;
  @Input() marriottCreatedAccount: MarriottEnrollmentResponse;
  @Input() isLoadingMarriottCreateForm: boolean;
  @Input() marriottCreateAccountShowStatus: MarriottCreateFormUIResponse;
  @Input() isLoadingPaxForm: boolean;
  @Input() set defaultData(defaultData: TravelerPaxResponse) {
    if (this.paxTravelers.find(treveler => treveler._id === defaultData?._id)) {
      this._defaultData = defaultData;
      if (this.hasDefaultPax) this.paxTraveler = this.defaultData;
    } else if (defaultData) this.removeDefaultPax.emit(this.travelerNumber);
  }

  @Output() updatePax: EventEmitter<TravelerPaxResponseVm> = new EventEmitter();
  @Output() createPax: EventEmitter<TravelerPax> = new EventEmitter();
  @Output() removePax: EventEmitter<TravelerPaxIdentificator> = new EventEmitter();
  @Output() selectPax: EventEmitter<TravelerPaxResponse> = new EventEmitter();
  @Output() selectLoyalty: EventEmitter<LoyaltyResponse> = new EventEmitter();
  @Output() createLoyalty: EventEmitter<LoyaltyCreateEvent> = new EventEmitter();
  @Output() updateLoyalty: EventEmitter<LoyaltyUpdateEvent> = new EventEmitter();
  @Output() removeLoyalty: EventEmitter<LoyaltyResponse> = new EventEmitter();
  @Output() amplitudeEvent: EventEmitter<AmplitudeEventParams> = new EventEmitter();
  @Output() removeDefaultPax: EventEmitter<number> = new EventEmitter<number>();
  @Output() createMarriottBonvoyAccount: EventEmitter<MarriottEnrollment> = new EventEmitter<MarriottEnrollment>();
  @Output() resetMarriottCreateAccountShowStatus: EventEmitter<void> = new EventEmitter<void>();

  public readonly icons = Icon;
  public readonly typographyFontSize = TypographyFontSize;
  public readonly typographyColor = TypographyColor;

  public showForm: boolean = false;
  public showList: boolean = false;
  public showFrequentPassengerForm: boolean = false;
  public showFrequentPassengerList: boolean = false;
  public showTravelersListModal: boolean = false;
  public showFrequentPassengerFormModal: boolean = false;
  public showFrequentPassengerListModal: boolean = false;
  public showPassportForm: boolean = false;
  public showPassportFormModal: boolean = false;
  public paxTraveler: TravelerPaxResponse;
  public paxLoyalty: LoyaltyResponse;
  public travelerError: boolean = false;
  public paxSectionErrors: FormErrors[] = [];
  public showMarriottProgramForm = false;
  public showMarriottProgramCreateForm = false;
  public paxTravelerHasBeenSelected = false;

  private _paxTraveler: TravelerPaxResponse;
  private _defaultData: TravelerPaxResponse;
  private _paxLoyalty: LoyaltyResponse;
  private subscriptions: Subscription[] = [];

  constructor(
    private domManipulateService: DomManipulateService,
    private paxFormService: PaxFormService,
    private changeDetector: ChangeDetectorRef,
    private translation: TranslocoService
  ) { }

  ngAfterViewInit(): void {
    const formGroup = this.form ? (this.form?.get(BOOKING_FORM_INPUTS.TRAVELERS) as UntypedFormArray) : null;
    const formItem = formGroup ? formGroup.at(this.travelerNumber) as UntypedFormGroup : null;
    if (formItem) {
      this.travelerError = formItem.status === INVALID;
      this.paxSectionErrors = this.paxFormService.getAllPaxErrors(formItem);
      this.subscriptions.push(
        formItem.statusChanges.pipe(
          distinctUntilChanged((current, preview) => isEqual(current, preview))).subscribe(status => {
          this.travelerError = status === INVALID;
          this.changeDetector.detectChanges();
        })
      );
      this.changeDetector.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe()); 
  }

  get defaultData(): TravelerPaxResponse {
    return this._defaultData; 
  }

  get defaultPaxFullName(): string {
    return `${this.defaultData.first_name} ${this.defaultData.last_name}`; 
  }

  get hasDefaultPax(): boolean {
    return !!this.defaultData || !isEmpty(this.defaultData); 
  }
  get sectionError(): boolean {
    return this.isSubmitted && !this.hasDefaultPax; 
  }
  get moreThanOneUser(): boolean {
    return this.paxTravelers?.length > 0; 
  }
  get travelerDynamicForm(): UntypedFormGroup {
    return this.addDynamicElement?.at(this.travelerNumber) as UntypedFormGroup; 
  }
  get addDynamicElement(): UntypedFormArray {
    return this.form?.get(BOOKING_FORM_INPUTS.TRAVELERS) as UntypedFormArray; 
  }
  get defaultAirline(): string {
    return this.paxFormService.defaultAirline(this.validator as AirValidationResponse); 
  }
  get showEditContainer(): boolean {
    return this.travelerNumber === 0 && !!this.paxTraveler && this.paxTraveler.isConfigDefault; 
  }
  get showDevicePassportForm(): boolean {
    return this.isMobile ? this.showPassportForm : this.showPassportFormModal; 
  }

  get travelerLabel(): string {
    return getBookingInfoTypeByTravelerInfoType(this.travelerInfoType);
  }

  get paxTypeTranslate(): string {
    return this.translation.translate(getTranslateKey(this.travelerInfoType));
  }

  get showDeviceLoyaltyForm(): boolean {
    return this.isMobile ? this.showFrequentPassengerForm : this.showFrequentPassengerFormModal;
  }

  get disabledDocumentInputs(): boolean {
    return !isEnabledPaxDocumentModule(this.businessRules, this.isAnInternationalTrip, this.productCode, this.productProvider);
  }

  get showMarriottRelatedContent(): boolean {
    return this.showMarriottProgramForm || this.showMarriottProgramCreateForm;
  }

  public toggleForm(isCreating: boolean = true): void {
    this.showForm = !this.showForm;
    if (this.showForm) {
      if (isCreating) {
        this.paxTraveler = null;
        this.amplitudeEvent.emit({ type: AMPLITUDE_EVENTS.SELECT_ADD_PAX, props: {} });
      } else this.amplitudeEvent.emit({ type: AMPLITUDE_EVENTS.SELECT_EDIT_PAX, props: {} });
      this.amplitudeEvent.emit({ type: AVO_EVENTS.VIEW_PAX_FORM, props: {} });
    }
  }

  public closeForm(): void {
    this.paxTraveler = this._paxTraveler;
    this.toggleForm();
  }

  public closeLoyalty(): void {
    this.paxLoyalty = this._paxLoyalty; this.toggleFrequenPassengerForm();
  }

  public toggleList(): void {
    if (this.isMobile) {
      this.showList = !this.showList;
    } else {
      this.showTravelersListModal = !this.showTravelersListModal;
    }
    if (this.showList || this.showTravelersListModal) {
      this.amplitudeEvent.emit({
        type: AMPLITUDE_EVENTS.SELECT_VIEW_PAX_LIST, props: {}
      });
    }
  }

  public doubleToggle(): void {
    this._paxTraveler = this.paxTraveler;
    this.toggleList();
    this.toggleForm();
  }

  public doubleToggleLoyalty(): void {
    this._paxLoyalty = this.paxLoyalty; this.toggleFrequentPassengerList(); this.toggleFrequenPassengerForm();
  }

  public toggleFrequenPassengerForm(isCreating: boolean = true): void {
    if (this.isMobile) {
      this.showFrequentPassengerForm = !this.showFrequentPassengerForm;
      this.manipulateDOM(!this.showFrequentPassengerForm);
      if (isCreating && this.showFrequentPassengerForm) this.paxLoyalty = null;
    } else {
      this.showFrequentPassengerFormModal = !this.showFrequentPassengerFormModal;
      this.manipulateDOM(!this.showFrequentPassengerFormModal);
      if (isCreating && this.showFrequentPassengerFormModal) this.paxLoyalty = null;
    }
  }

  public toggleFrequentPassengerList(): void {
    if (this.isMobile) {
      this.showFrequentPassengerList = !this.showFrequentPassengerList;
      this.manipulateDOM(!this.showFrequentPassengerList);
    } else {
      this.showFrequentPassengerListModal = !this.showFrequentPassengerListModal;
      this.manipulateDOM(!this.showFrequentPassengerListModal);
    }
  }

  public toggleMarriottProgramForm(): void {
    this.showMarriottProgramForm = !this.showMarriottProgramForm;
  }

  public toggleMarriottProgramCreateForm(): void {
    if (this.showMarriottProgramForm) {
      this.toggleMarriottProgramForm();
    }
    this.resetMarriottCreateAccountShowStatus.emit();
    this.showMarriottProgramCreateForm = !this.showMarriottProgramCreateForm;
  }

  public openLoyaltyByResumed(selectedLoyalty: LoyaltyResponse): void {
    if (!isEmpty(selectedLoyalty)) {
      this.paxLoyalty = selectedLoyalty;
      this.toggleFrequenPassengerForm(false);
    } else this.toggleFrequenPassengerForm();
  }

  public editPax(traveler: TravelerPaxResponse): void {
    this._paxTraveler = this.paxTraveler;
    this.paxTraveler = traveler;
    this.toggleList();
    this.toggleForm(false);
  }

  public updateTravelerPax(traveler: TravelerPaxResponseVm): void {
    this.updatePax.emit({
      ...this.paxTraveler,
      ...traveler
    });
  }

  public createTravelerPax(traveler: TravelerPax): void {
    this.createPax.emit(traveler);
  }

  public selectPaxEvent(traveler: TravelerPaxResponse): void {
    this.paxTraveler = traveler;
    this.paxTravelerHasBeenSelected = true;
    this.selectPax.emit(traveler);
    this.toggleList();
    this.changeDetector.detectChanges();
  }

  public travelerHasSuccessfullyChanged(): void {
    this.paxTravelerHasBeenSelected = false;
    this.changeDetector.detectChanges();
  }

  public removePaxEvent(item: TravelerPaxIdentificator): void {
    this.removePax.emit(item);
    this.toggleList();
  }

  public createLoyaltyEvent(loyalty: LoyaltyPayload): void {
    this.toggleFrequenPassengerForm();
    const createLoyalty = !!this.paxTraveler && !!this.paxTraveler._id &&
      !this.paxFormService.isStaticTravelerId(this.paxTraveler._id);
    this.createLoyalty.emit({ loyalty: { ...loyalty, traveler: this.paxTraveler._id }, create: createLoyalty });
  }

  public selectLoyaltyEvent(loyalty: LoyaltyResponse): void {
    this.selectLoyalty.emit(loyalty); this.toggleFrequentPassengerList();
  }

  public editLoyaltyEvent(loyalty: LoyaltyResponse): void {
    this._paxLoyalty = this.paxLoyalty; this.paxLoyalty = loyalty;
    this.toggleFrequentPassengerList();
    this.toggleFrequenPassengerForm(false);
  }

  public updateLoyaltyEvent(loyalty: LoyaltyResponse): void {
    this.paxLoyalty = loyalty; this.updateLoyalty.emit({ loyalty, update: !!loyalty._id });
    this.toggleFrequenPassengerForm(false);
  }

  public removeLoyaltyEvent(loyalty: LoyaltyResponse): void {
    this.removeLoyalty.emit(loyalty); this.toggleFrequentPassengerList();
  }

  public togglePassportForm(): void {
    if (this.isMobile) {
      this.showPassportForm = !this.showPassportForm;
      this.manipulateDOM(!this.showPassportForm);
    } else {
      this.showPassportFormModal = !this.showPassportFormModal;
      this.manipulateDOM(!this.showPassportFormModal);
    }
  }

  public updateTravelerDocumentPassport(): void {
    const paxFormData = this.travelerDynamicForm.value;
    const traveler: TravelerPaxResponseVm = {
      ...this.paxTraveler, document_number: paxFormData[TRAVELER_FORM_INPUTS.DOCUMENT_NUMBER],
      configuration_document_id: paxFormData[TRAVELER_FORM_INPUTS.DOCUMENT_ID]
    };
    this.updatePax.emit(traveler); this.togglePassportForm();
  }

  public updateMarriottBonvoyNumber(value: string): void {
    const traveler: TravelerPaxResponse = {
      ...this.paxTraveler,
      bonvoy_number: value
    }
    this.travelerDynamicForm?.patchValue({
      [TRAVELER_FORM_INPUTS.BONVOY_NUMBER]: value
    });
    this.updatePax.emit(traveler);
  }

  public mapMarriottBonvoyAccount(email: string) {
    const payload: MarriottEnrollment  = {
      name: {
        surname: this.defaultData.last_name,
        givenName: this.defaultData.first_name
      },
      contact: {
        email: email,
        phone: this.defaultData.phone
      }
    };
    this.createMarriottBonvoyAccount.emit(payload);
  }

  private manipulateDOM(state: boolean): void {
    this.domManipulateService.setBodyScroll(state);
  }
}
